import type Phaser from 'phaser';
import type { TileId } from '../../content/tiles';
import { Tile } from '../../sim/grid';
import type { Simulation } from '../../sim/simulation';
import { TILE_SIZE } from '../layout';
import { thumbColor } from '../ui/chrome';

/** A dot on the minimap at a tile (fractional for something between tiles). */
export interface MinimapMark {
  x: number;
  y: number;
  color: number;
}

/** The minimap's largest size on screen, in pixels. */
const MAX_W = 48;
const MAX_H = 64;
/** Gap from the corner of the lawn view. */
const INSET = 3;
const ORANGE = 0xff8a1f;
const INK = 0x10180c;

/**
 * A minimap for lawns bigger than the screen (owner playtest note, lm-t5v.12): the whole lawn in
 * a top corner of the lawn view, mowed grass filling in as you go, an orange box for what is on
 * screen and a blinking dot for the mower. When the mower heads into the top of the view on the
 * map's side it fades over to the other top corner, out of the way (lm-c7h); it also fades while
 * the mower is right under it.
 * Fixed to the camera; above the world's light, so it reads the same in every world.
 */
export class Minimap {
  private readonly base: Phaser.GameObjects.Graphics;
  private readonly overlay: Phaser.GameObjects.Graphics;
  private readonly px: number;
  private readonly w: number;
  private readonly h: number;
  private dirty = true;
  private alpha = 0.92;
  /** Which top corner the map sits in, and the one it is fading over to. */
  private side: 'left' | 'right' = 'right';
  private wantSide: 'left' | 'right' = 'right';
  private drawnAt = -1;
  /** Other things to show, by tile (a maze match: the middle, and the friend's mower). */
  private marks: readonly MinimapMark[] = [];

  /** Whether a lawn of this size (pixels) needs a minimap in a view of this size. */
  static wanted(mapW: number, mapH: number, viewW: number, viewH: number): boolean {
    return mapW > viewW || mapH > viewH + 4 * TILE_SIZE;
  }

  constructor(
    scene: Phaser.Scene,
    private sim: Simulation,
    private readonly art: readonly TileId[],
    /** Tiles under tree crowns, drawn in shade. */
    private readonly crowns: ReadonlySet<number>,
    depth: number,
  ) {
    const { width, height } = sim.grid;
    this.w = width;
    this.h = height;
    this.px = Math.max(1, Math.min(3, Math.floor(MAX_W / width), Math.floor(MAX_H / height)));
    this.base = scene.add.graphics().setScrollFactor(0).setDepth(depth);
    this.overlay = scene.add
      .graphics()
      .setScrollFactor(0)
      .setDepth(depth + 1);
  }

  /** Reads a new copy of the lawn from now on (head to head rebuilds it on a late turn). */
  resync(sim: Simulation): void {
    this.sim = sim;
    this.dirty = true;
  }

  /** Other things to show on the map from now on, by tile. */
  setMarks(marks: readonly MinimapMark[]): void {
    this.marks = marks;
  }

  /** The lawn changed (a tile mowed or cleared): redraw it next frame. */
  invalidate(): void {
    this.dirty = true;
  }

  /** Places the map in the view's corner and draws the view box and the mower. */
  render(cam: Phaser.Cameras.Scene2D.Camera, mowerX: number, mowerY: number, now: number): void {
    const mw = this.w * this.px;
    const mh = this.h * this.px;
    const sx = mowerX - cam.scrollX;
    const sy = mowerY - cam.scrollY;
    // Heading into the top of the view on the map's side: it moves to the other top corner
    // (with a margin either way of the middle, so it never flickers back and forth).
    if (sy < mh + 48) {
      if (this.side === 'right' && sx > cam.width * 0.55) this.wantSide = 'left';
      if (this.side === 'left' && sx < cam.width * 0.45) this.wantSide = 'right';
    }
    const moving = this.wantSide !== this.side;
    if (moving && this.alpha < 0.05) this.side = this.wantSide;
    const x0 = this.side === 'right' ? cam.width - mw - INSET : INSET;
    const y0 = INSET;
    // Fade while moving corners, or while the mower is right under the map.
    const under = sx > x0 - 12 && sx < x0 + mw + 12 && sy > y0 - 12 && sy < y0 + mh + 12;
    const target = moving ? 0 : under ? 0.25 : 0.92;
    this.alpha += (target - this.alpha) * (moving ? 0.35 : 0.2);
    this.base.setAlpha(this.alpha);
    this.overlay.setAlpha(this.alpha);
    if (this.dirty || this.drawnAt !== x0) this.drawBase(x0, y0);
    const g = this.overlay.clear();
    const scale = this.px / TILE_SIZE;
    // The view box: what is on screen, clamped to the map.
    const vx = Math.max(0, Math.round(cam.scrollX * scale));
    const vy = Math.max(0, Math.round(cam.scrollY * scale));
    const vw = Math.min(mw - vx, Math.round(cam.width * scale));
    const vh = Math.min(mh - vy, Math.round(cam.height * scale));
    g.lineStyle(1, ORANGE).strokeRect(x0 + vx + 0.5, y0 + vy + 0.5, vw - 1, vh - 1);
    for (const mark of this.marks) {
      g.fillStyle(mark.color).fillRect(
        x0 + Math.floor(mark.x * this.px),
        y0 + Math.floor(mark.y * this.px),
        Math.max(2, this.px),
        Math.max(2, this.px),
      );
    }
    // The mower: a blinking dot.
    const mx = x0 + Math.floor((mowerX / TILE_SIZE) * this.px);
    const my = y0 + Math.floor((mowerY / TILE_SIZE) * this.px);
    const size = Math.max(2, this.px);
    g.fillStyle(Math.floor(now / 300) % 2 === 0 ? 0xffffff : ORANGE).fillRect(
      mx - Math.floor(size / 2),
      my - Math.floor(size / 2),
      size,
      size,
    );
  }

  private drawBase(x0: number, y0: number): void {
    this.dirty = false;
    this.drawnAt = x0;
    const g = this.base.clear();
    const { px, w, h } = this;
    g.fillStyle(INK).fillRect(x0 - 1, y0 - 1, w * px + 2, h * px + 2);
    const grid = this.sim.grid;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        const art = this.art[i] ?? 'grass';
        const kind = grid.tileAt(x, y);
        const color =
          kind === Tile.Mowed
            ? thumbColor(art, x, y, true)
            : kind === Tile.Ground && art !== 'path' && art !== 'sand'
              ? thumbColor('path', x, y, false)
              : thumbColor(art, x, y, false);
        // Under a tree crown: shaded, but still showing what is mowed.
        g.fillStyle(this.crowns.has(i) ? shade(color) : color).fillRect(
          x0 + x * px,
          y0 + y * px,
          px,
          px,
        );
      }
    }
  }
}

/** A color in a tree's shade: darker, greener. */
function shade(c: number): number {
  const r = (c >> 16) & 0xff;
  const g = (c >> 8) & 0xff;
  const b = c & 0xff;
  return (Math.round(r * 0.5) << 16) | (Math.round(g * 0.62) << 8) | Math.round(b * 0.5);
}
